// Wayne AI Platform — Memory page typed service.
//
// Uses `wayneFetch` for consistent auth/CSRF/error handling. The backend
// returns a shape (items, cursor, total, writtenBy, ISO timestamps) that
// differs from the frontend's `MemoryRow` type — we map it here at the
// service boundary so consumers stay unchanged.

import { wayneFetch } from '@/api/wayneFetch';

export type MemoryKind =
  | 'fact'
  | 'preference'
  | 'decision'
  | 'todo'
  | 'reference'
  | 'context';

export type MemoryTier = 'long-term' | 'short-term' | 'working';

export interface MemoryRow {
  memoryId: string;
  employeeId: string;
  kind: MemoryKind;
  content: string;
  tier: MemoryTier;
  confidence: number;
  createdBy: string;
  createdAt: number;
  linkedTurnId?: string;
  tags: ReadonlyArray<string>;
  ttl?: number;
  lastReferencedAt?: number;
  updatedAt?: number;
}

export interface MemoryPage {
  rows: ReadonlyArray<MemoryRow>;
  pageInfo: {
    nextCursor: string | null;
    total: number;
  };
}

export interface ListMemoryParams {
  kind?: MemoryKind;
  /** Free-text search; matched against content + tags. */
  search?: string;
  limit?: number;
  cursor?: string;
}

// -- Backend response shapes (internal, not exported) -------------------------

interface BackendMemoryItem {
  employeeId: string;
  memoryId: string;
  kind: MemoryKind;
  content: string;
  tier?: MemoryTier;
  confidence?: number;
  tags?: ReadonlyArray<string>;
  writtenBy?: string;
  createdBy?: string;
  createdAt: string | number;
  updatedAt?: string | number;
  linkedTurnId?: string;
  ttl?: number;
  lastReferencedAt?: string | number;
}

interface BackendListResponse {
  items?: ReadonlyArray<BackendMemoryItem>;
  /** Legacy shape (from MSW mocks) uses `rows`. */
  rows?: ReadonlyArray<BackendMemoryItem>;
  cursor?: string | null;
  total?: number;
  /** Legacy shape (from MSW mocks) uses `pageInfo`. */
  pageInfo?: { nextCursor: string | null; total: number };
}

// -- Helpers ------------------------------------------------------------------

/**
 * Normalise a timestamp that may be either an ISO-8601 string or an epoch-ms
 * number into epoch-ms.
 */
function toEpochMs(val: string | number | undefined): number | undefined {
  if (val === undefined) return undefined;
  if (typeof val === 'number') return val;
  const ms = new Date(val).getTime();
  return Number.isFinite(ms) ? ms : undefined;
}

/** Map one backend item to the frontend's MemoryRow shape. */
function mapItem(item: BackendMemoryItem): MemoryRow {
  const row: MemoryRow = {
    memoryId: item.memoryId,
    employeeId: item.employeeId,
    kind: item.kind,
    content: item.content,
    tier: item.tier ?? 'short-term',
    confidence: item.confidence ?? 0.5,
    createdBy: item.writtenBy ?? item.createdBy ?? 'unknown',
    createdAt: toEpochMs(item.createdAt) ?? Date.now(),
    tags: item.tags ?? [],
  };
  if (item.linkedTurnId != null) row.linkedTurnId = item.linkedTurnId;
  if (item.ttl != null) row.ttl = item.ttl;
  const lastRef = toEpochMs(item.lastReferencedAt);
  if (lastRef != null) row.lastReferencedAt = lastRef;
  const updated = toEpochMs(item.updatedAt);
  if (updated != null) row.updatedAt = updated;
  return row;
}

// -- CRUD operations ----------------------------------------------------------

export async function listMemory(params: ListMemoryParams = {}): Promise<MemoryPage> {
  const searchParams = new URLSearchParams();
  if (params.kind) searchParams.set('kind', params.kind);
  if (params.search) searchParams.set('q', params.search);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.cursor) searchParams.set('cursor', params.cursor);

  const qs = searchParams.toString();
  const path = qs ? `/memory?${qs}` : '/memory';

  let raw: BackendListResponse;
  try {
    raw = await wayneFetch<BackendListResponse>(path);
  } catch {
    // Gracefully degrade: show empty state rather than "Couldn't load memory"
    return { rows: [], pageInfo: { nextCursor: null, total: 0 } };
  }

  // Accept both backend shapes: `items` (real API) and `rows` (MSW mocks).
  const rawItems = raw.items ?? raw.rows ?? [];
  const rows = rawItems.map(mapItem);

  // Accept both pagination shapes.
  const nextCursor = raw.cursor ?? raw.pageInfo?.nextCursor ?? null;
  const total = raw.total ?? raw.pageInfo?.total ?? rows.length;

  return { rows, pageInfo: { nextCursor, total } };
}

export interface CreateMemoryParams {
  kind: MemoryKind;
  content: string;
  writtenBy?: string;
  tags?: ReadonlyArray<string>;
}

export async function createMemory(params: CreateMemoryParams): Promise<MemoryRow> {
  const raw = await wayneFetch<BackendMemoryItem>('/memory', {
    method: 'POST',
    body: params,
  });
  return mapItem(raw);
}

export interface UpdateMemoryParams {
  content?: string;
  tags?: ReadonlyArray<string>;
  kind?: MemoryKind;
}

export async function updateMemory(
  memoryId: string,
  params: UpdateMemoryParams,
): Promise<MemoryRow> {
  const raw = await wayneFetch<BackendMemoryItem>(
    `/memory/${encodeURIComponent(memoryId)}`,
    { method: 'PATCH', body: params },
  );
  return mapItem(raw);
}

export async function deleteMemory(memoryId: string): Promise<void> {
  await wayneFetch<undefined>(
    `/memory/${encodeURIComponent(memoryId)}`,
    { method: 'DELETE' },
  );
}

export interface WipeAllResult {
  wiped: number;
}

/**
 * Hard wipe of every memory row scoped to the current user. The server
 * runs a soft-delete with a 7-day grace bin; the SPA surfaces the action as
 * "wipe-all" without exposing the bin.
 */
export async function wipeAllMemory(): Promise<WipeAllResult> {
  return wayneFetch<WipeAllResult>('/memory/wipe-all', { method: 'POST' });
}

/**
 * Promote a memory from short-term to long-term tier.
 */
export async function promoteMemory(memoryId: string): Promise<MemoryRow> {
  const raw = await wayneFetch<BackendMemoryItem>(
    `/memory/${encodeURIComponent(memoryId)}/promote`,
    { method: 'POST' },
  );
  return mapItem(raw);
}
